import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2, MessageSquare, CheckSquare, Pencil, ChevronRight, Archive, ArchiveRestore, List, Columns3, CheckCheck } from "lucide-react";
import TaskBoard from "@/components/TaskBoard";
import { Reply } from "@/components/ReplyEdit";
import { VisibilityBadge, DiscussionAccessButton, AccessPicker } from "@/components/DiscussionAccess";
import { PRIORITY, PriorityBadge, DueBadge } from "@/components/TaskMeta";
import ImageUploader, { ImageGrid } from "@/components/ImageUploader";
import AttachmentUploader, { AttachmentList } from "@/components/AttachmentUploader";
import LikeButton from "@/components/LikeButton";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  todo: { label: "To Do", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  in_progress: { label: "In Progress", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  done: { label: "Done", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

function PriorityDueFields({ priority, setPriority, dueDate, setDueDate, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label>Priority</Label>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid={`${prefix}-priority-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PRIORITY).map(([k, v]) => (
              <SelectItem key={k} value={k} data-testid={`${prefix}-priority-${k}`}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Due date</Label>
        <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid={`${prefix}-due-input`} />
      </div>
    </div>
  );
}

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [users, setUsers] = useState([]);

  const loadAll = async () => {
    try {
      const [p, t, d, u] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/tasks`),
        api.get(`/projects/${projectId}/discussions`),
        api.get(`/users`),
      ]);
      setProject(p.data);
      setTasks(t.data);
      setDiscussions(d.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    api
      .post("/notifications/read", { project_id: projectId })
      .then(() => window.dispatchEvent(new Event("notifications-updated")))
      .catch(() => {});
  }, [projectId]);

  if (!project) return <div className="text-slate-400 py-20 text-center">Loading…</div>;

  const memberUsers = users.filter((u) => (project.members || []).includes(u.id));
  const isGuest = project.my_access === "guest";
  const hasUnread = tasks.some((t) => t.unread) || discussions.some((d) => d.unread);

  const markAllRead = async () => {
    try {
      await api.post("/read-all", { project_id: projectId });
      window.dispatchEvent(new Event("notifications-updated"));
      toast.success("Project marked as read");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
        data-testid="back-to-projects"
      >
        <ArrowLeft className="h-4 w-4" /> All projects
      </Link>

      <div className="mt-3 mb-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {project.name}
          </h1>
          {hasUnread && (
            <Button variant="outline" onClick={markAllRead} data-testid="project-mark-all-read-btn">
              <CheckCheck className="h-4 w-4 mr-1.5" /> Mark all as read
            </Button>
          )}
        </div>
        {project.description && <p className="text-slate-500 mt-2 max-w-2xl whitespace-pre-wrap">{project.description}</p>}
      </div>

      <Tabs defaultValue={isGuest ? "discussions" : "tasks"}>
        <TabsList className="h-auto bg-slate-100 p-1 rounded-lg">
          {!isGuest && (
          <TabsTrigger value="tasks" className="h-auto py-1.5 px-4" data-testid="tab-tasks">
            <CheckSquare className="h-4 w-4 mr-1.5" /> Tasks ({tasks.length})
          </TabsTrigger>
          )}
          <TabsTrigger value="discussions" className="h-auto py-1.5 px-4" data-testid="tab-discussions">
            <MessageSquare className="h-4 w-4 mr-1.5" /> Discussions ({discussions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-6">
          <TasksTab
            projectId={projectId}
            tasks={tasks}
            users={memberUsers}
            reload={loadAll}
            isAdmin={isAdmin}
          />
        </TabsContent>

        <TabsContent value="discussions" className="mt-6">
          <DiscussionsTab projectId={projectId} discussions={discussions} reload={loadAll} isAdmin={isAdmin} users={users} isGuest={isGuest} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TasksTab({ projectId, tasks, users, reload, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState("unassigned");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [view, setView] = useState("list");
  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [archivedTasks, setArchivedTasks] = useState([]);

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const loadArchived = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/tasks?include_archived=true`);
      setArchivedTasks(res.data.filter((t) => t.archived));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleShowArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next) loadArchived();
  };

  const markSeen = async (id) => {
    try {
      await api.post(`/tasks/${id}/seen`);
      reload();
    } catch (e) {}
  };

  const archiveTask = async (id, val) => {
    try {
      await api.patch(`/tasks/${id}`, { archived: val });
      toast.success(val ? "Task archived" : "Task restored");
      reload();
      if (showArchived) loadArchived();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/tasks`, {
        title,
        description,
        assignee_id: assignee === "unassigned" ? null : assignee,
        priority,
        due_date: dueDate || null,
        images,
        attachments,
      });
      toast.success("Task created");
      setTitle("");
      setDescription("");
      setAssignee("unassigned");
      setPriority("medium");
      setDueDate("");
      setImages([]);
      setAttachments([]);
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/tasks/${id}`, { status });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changeAssignee = async (id, val) => {
    try {
      await api.patch(`/tasks/${id}`, { assignee_id: val === "unassigned" ? null : val });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success("Task deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openFromBoard = (t) => {
    setView("list");
    setExpanded((prev) => new Set(prev).add(t.id));
    if (t.unread) markSeen(t.id);
    setTimeout(() => document.querySelector(`[data-testid="task-card-${t.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="task-view-toggle">
            {[["list", List, "List"], ["board", Columns3, "Board"]].map(([k, Icon, label]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${view === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}
                data-testid={`task-view-${k}`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          <button
            onClick={toggleShowArchived}
            className="text-sm text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-1.5"
            data-testid="toggle-archived-btn"
          >
            <Archive className="h-4 w-4" />
            {showArchived ? "Hide archived" : "Show archived"}
          </button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-700 text-white" data-testid="new-task-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New task
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create task</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="task-form">
              <div className="space-y-2">
                <Label htmlFor="t-title">Title</Label>
                <Input
                  id="t-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="t-desc">Description</Label>
                <Textarea
                  id="t-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  data-testid="task-desc-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Assign to</Label>
                <Select value={assignee} onValueChange={setAssignee}>
                  <SelectTrigger data-testid="task-assignee-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`assignee-option-${u.id}`}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <PriorityDueFields priority={priority} setPriority={setPriority} dueDate={dueDate} setDueDate={setDueDate} prefix="task" />
              <div className="space-y-2">
                <Label>Images</Label>
                <ImageUploader images={images} setImages={setImages} testid="task-image-uploader" />
              </div>
              <div className="space-y-2">
                <Label>Attachments</Label>
                <AttachmentUploader attachments={attachments} setAttachments={setAttachments} testid="task-attachment-uploader" />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-700 text-white"
                  data-testid="task-submit-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create task"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {tasks.length === 0 ? (
        <EmptyState label="No tasks yet" hint="Create a task and assign it to a teammate." />
      ) : view === "board" ? (
        <TaskBoard tasks={tasks} onMove={changeStatus} onOpen={openFromBoard} />
      ) : (
        <div className="space-y-4" data-testid="tasks-list">
          {tasks.map((t) => (
            <div
              key={t.id}
              className={`bg-white border rounded-xl p-6 animate-fade-in-up ${t.unread ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"}`}
              data-testid={`task-card-${t.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div
                  className="min-w-0 flex items-start gap-2 cursor-pointer"
                  onClick={() => {
                    const wasClosed = !expanded.has(t.id);
                    toggleExpand(t.id);
                    if (wasClosed && t.unread) markSeen(t.id);
                  }}
                  data-testid={`task-toggle-${t.id}`}
                >
                  <ChevronRight
                    className={`h-5 w-5 text-slate-400 mt-0.5 shrink-0 transition-transform ${expanded.has(t.id) ? "rotate-90" : ""}`}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {t.unread && (
                        <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" data-testid={`task-unread-${t.id}`} />
                      )}
                      <h4 className="font-heading text-lg font-medium text-slate-900">{t.title}</h4>
                    </div>
                    {!expanded.has(t.id) && (
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className={`text-xs font-medium rounded-full border px-2 py-0.5 ${STATUS[t.status].cls}`}>
                          {STATUS[t.status].label}
                        </span>
                        <PriorityBadge priority={t.priority} testid={`task-priority-${t.id}`} />
                        <DueBadge due={t.due_date} done={t.status === "done"} testid={`task-due-${t.id}`} />
                        {t.assignee_name && <span className="text-xs text-slate-400">{t.assignee_name}</span>}
                        {(t.comments?.length || 0) > 0 && (
                          <span className="text-xs text-slate-400">{t.comments.length} replies</span>
                        )}
                      </div>
                    )}
                    {expanded.has(t.id) && t.description && (
                      <p className="text-sm text-slate-500 mt-1 whitespace-pre-wrap">{t.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <TaskEditButton task={t} users={users} reload={reload} />
                  <button
                    onClick={() => archiveTask(t.id, true)}
                    className="text-slate-300 hover:text-amber-600 transition-colors p-1"
                    aria-label="Archive task"
                    data-testid={`archive-task-${t.id}`}
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(t.id)}
                    className="text-slate-300 hover:text-red-500 transition-colors p-1"
                    aria-label="Delete task"
                    data-testid={`delete-task-${t.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {expanded.has(t.id) && (
              <>
              <ImageGrid images={t.images} />
              <AttachmentList attachments={t.attachments} />

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <Select value={t.status} onValueChange={(v) => changeStatus(t.id, v)}>
                  <SelectTrigger
                    className={`h-8 w-auto gap-2 text-xs font-medium rounded-full border ${STATUS[t.status].cls}`}
                    data-testid={`task-status-${t.id}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={t.assignee_id || "unassigned"}
                  onValueChange={(v) => changeAssignee(t.id, v)}
                >
                  <SelectTrigger
                    className="h-8 w-auto gap-2 text-xs rounded-full border-slate-200"
                    data-testid={`task-assignee-${t.id}`}
                  >
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <PriorityBadge priority={t.priority} testid={`task-priority-open-${t.id}`} />
                <DueBadge due={t.due_date} done={t.status === "done"} testid={`task-due-open-${t.id}`} />
                <LikeButton likes={t.likes} endpoint={`/tasks/${t.id}/like`} reload={reload} testid={`task-like-${t.id}`} />
                <span className="text-xs text-slate-400 ml-auto">by {t.created_by_name}</span>
              </div>

              <TaskComments task={t} reload={reload} />
              </>
              )}
            </div>
          ))}
        </div>
      )}

      {showArchived && (
        <div className="mt-8">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-3">
            Archived ({archivedTasks.length})
          </p>
          {archivedTasks.length === 0 ? (
            <p className="text-sm text-slate-400">No archived tasks.</p>
          ) : (
            <div className="space-y-3" data-testid="archived-tasks-list">
              {archivedTasks.map((t) => (
                <div
                  key={t.id}
                  className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4"
                  data-testid={`archived-task-${t.id}`}
                >
                  <div className="min-w-0">
                    <h4 className="font-heading text-base font-medium text-slate-700 truncate">{t.title}</h4>
                    <span className={`text-xs font-medium rounded-full border px-2 py-0.5 ${STATUS[t.status].cls}`}>
                      {STATUS[t.status].label}
                    </span>
                  </div>
                  <button
                    onClick={() => archiveTask(t.id, false)}
                    className="text-sm text-slate-500 hover:text-emerald-600 transition-colors inline-flex items-center gap-1.5 shrink-0"
                    data-testid={`unarchive-task-${t.id}`}
                  >
                    <ArchiveRestore className="h-4 w-4" /> Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiscussionsTab({ projectId, discussions, reload, isAdmin, users, isGuest }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState([]);
  const [vis, setVis] = useState("everyone");
  const [visPicked, setVisPicked] = useState([]);

  const loadArchived = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/discussions?include_archived=true`);
      setArchived(res.data.filter((d) => d.archived));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const archiveDiscussion = async (id, val) => {
    try {
      await api.patch(`/discussions/${id}`, { archived: val });
      toast.success(val ? "Discussion archived" : "Discussion restored");
      reload();
      if (showArchived) loadArchived();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/discussions`, { title, body, images, attachments, visibility: vis, user_ids: visPicked });
      toast.success("Discussion posted");
      setTitle("");
      setBody("");
      setImages([]);
      setAttachments([]);
      setVis("everyone");
      setVisPicked([]);
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this discussion?")) return;
    try {
      await api.delete(`/discussions/${id}`);
      toast.success("Discussion deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => { const n = !showArchived; setShowArchived(n); if (n) loadArchived(); }}
          className="text-sm text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-1.5"
          data-testid="toggle-archived-discussions-btn"
        >
          <Archive className="h-4 w-4" />
          {showArchived ? "Hide archived" : "Show archived"}
        </button>
        {!isGuest && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-700 text-white" data-testid="new-discussion-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New discussion
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Start a discussion</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="discussion-form">
              <div className="space-y-2">
                <Label htmlFor="d-title">Title</Label>
                <Input
                  id="d-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  data-testid="discussion-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="d-body">Message</Label>
                <Textarea
                  id="d-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  data-testid="discussion-body-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Images</Label>
                <ImageUploader images={images} setImages={setImages} testid="discussion-image-uploader" />
              </div>
              <div className="space-y-2">
                <Label>Attachments</Label>
                <AttachmentUploader attachments={attachments} setAttachments={setAttachments} testid="discussion-attachment-uploader" />
              </div>
              {isAdmin && (
                <div className="space-y-2" data-testid="discussion-create-access">
                  <Label>Who can see this?</Label>
                  <AccessPicker mode={vis} setMode={setVis} picked={visPicked} setPicked={setVisPicked} users={users} />
                </div>
              )}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-700 text-white"
                  data-testid="discussion-submit-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {discussions.length === 0 ? (
        <EmptyState label="No discussions yet" hint="Start a conversation and share images with the team." />
      ) : (
        <div className="space-y-4" data-testid="discussions-list">
          {discussions.map((d) => (
            <DiscussionCard key={d.id} d={d} users={users} onDelete={() => remove(d.id)} onArchive={() => archiveDiscussion(d.id, true)} reload={reload} />
          ))}
        </div>
      )}

      {showArchived && (
        <div className="mt-8">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-3">Archived ({archived.length})</p>
          {archived.length === 0 ? (
            <p className="text-sm text-slate-400">No archived discussions.</p>
          ) : (
            <div className="space-y-3" data-testid="archived-discussions-list">
              {archived.map((d) => (
                <div key={d.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`archived-discussion-${d.id}`}>
                  <div className="min-w-0">
                    <h4 className="font-heading text-base font-medium text-slate-700 truncate">{d.title}</h4>
                    <span className="text-xs text-slate-400">by {d.created_by_name} · {(d.comments || []).length} replies</span>
                  </div>
                  <button
                    onClick={() => archiveDiscussion(d.id, false)}
                    className="text-sm text-slate-500 hover:text-emerald-600 transition-colors inline-flex items-center gap-1.5 shrink-0"
                    data-testid={`unarchive-discussion-${d.id}`}
                  >
                    <ArchiveRestore className="h-4 w-4" /> Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DiscussionCard({ d, users, onDelete, onArchive, reload }) {
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [baseline, setBaseline] = useState(d.seen_at_me || "");
  const { user, isAdmin } = useAuth();
  const canManage = isAdmin || d.created_by === user?.id;
  const comments = d.comments || [];
  const isNewReply = (c) => c.created_by !== user?.id && !!c.created_at && (!baseline || c.created_at > baseline);

  const toggle = async () => {
    const opening = !expanded;
    setExpanded(opening);
    if (opening) {
      setBaseline(d.seen_at_me || "");
      if (d.unread) {
        try {
          await api.post(`/discussions/${d.id}/seen`);
          reload();
        } catch (e) {}
      }
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!body.trim() && images.length === 0 && attachments.length === 0) return;
    setSending(true);
    try {
      await api.post(`/discussions/${d.id}/comments`, { body, images, attachments });
      setBody("");
      setImages([]);
      setAttachments([]);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`bg-white border rounded-xl p-6 animate-fade-in-up ${d.unread ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"}`}
      data-testid={`discussion-card-${d.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="min-w-0 flex items-start gap-2 cursor-pointer"
          onClick={toggle}
          data-testid={`discussion-toggle-${d.id}`}
        >
          <ChevronRight
            className={`h-5 w-5 text-slate-400 mt-0.5 shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {d.unread && <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" data-testid={`discussion-unread-${d.id}`} />}
              <h4 className="font-heading text-lg font-medium text-slate-900">{d.title}</h4>
              <VisibilityBadge d={d} />
            </div>
            {!expanded && (
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-xs text-slate-400">by {d.created_by_name}</span>
                <span className="text-xs text-slate-300">·</span>
                <span className="text-xs text-slate-400">
                  {comments.length} {comments.length === 1 ? "reply" : "replies"}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isAdmin && <DiscussionAccessButton d={d} users={users} reload={reload} />}
          {canManage && (<>
          <DiscussionEditButton d={d} reload={reload} />
          <button
            onClick={onArchive}
            className="text-slate-300 hover:text-amber-600 transition-colors p-1"
            aria-label="Archive discussion"
            data-testid={`archive-discussion-${d.id}`}
          >
            <Archive className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="text-slate-300 hover:text-red-500 transition-colors p-1"
            aria-label="Delete discussion"
            data-testid={`delete-discussion-${d.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          </>)}
        </div>
      </div>

      {expanded && (
      <>
      {d.body && <p className="text-slate-600 mt-2 whitespace-pre-wrap">{d.body}</p>}
      <ImageGrid images={d.images} />
      <AttachmentList attachments={d.attachments} />
      <div className="flex items-center gap-3 mt-4">
        <LikeButton likes={d.likes} endpoint={`/discussions/${d.id}/like`} reload={reload} testid={`discussion-like-${d.id}`} />
        <span className="text-xs text-slate-400">by {d.created_by_name}</span>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-3">
          {comments.length} {comments.length === 1 ? "reply" : "replies"}
        </p>

        {comments.length > 0 && (
          <div className="space-y-4 mb-4" data-testid={`comments-${d.id}`}>
            {comments.map((c) => (
              <div key={c.id} className={`flex gap-3 rounded-lg -mx-2 px-2 py-1.5 transition-colors ${isNewReply(c) ? "bg-red-50 ring-1 ring-red-200" : ""}`} data-testid={`comment-${c.id}`}>
                <div className={`h-8 w-8 rounded-full ${c.created_by_role === "admin" ? "bg-amber-500" : "bg-indigo-600"} text-white text-xs flex items-center justify-center shrink-0`}>
                  {c.created_by_name?.[0] || "U"}
                </div>
                <div className="min-w-0 flex-1">
                  <Reply c={c} endpoint={`/discussions/${d.id}/comments/${c.id}`} reload={reload} header={<>
                    <p className="text-sm font-medium text-slate-900">{c.created_by_name}</p>
                    <span className="text-xs text-slate-400" data-testid={`discussion-comment-date-${c.id}`}>{formatDate(c.created_at)}</span>
                    {isNewReply(c) && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600 bg-red-100 rounded-full px-1.5 py-0.5" data-testid={`discussion-comment-new-${c.id}`}>New</span>
                    )}
                  </>} />
                  <ImageGrid images={c.images} />
                  <AttachmentList attachments={c.attachments} />
                  <div className="mt-2">
                    <LikeButton likes={c.likes} endpoint={`/discussions/${d.id}/comments/${c.id}/like`} reload={reload} testid={`discussion-comment-like-${c.id}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={addComment} className="space-y-3" data-testid={`comment-form-${d.id}`}>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write a reply…"
            rows={2}
            data-testid={`comment-input-${d.id}`}
          />
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-2">
              <ImageUploader images={images} setImages={setImages} testid={`comment-image-uploader-${d.id}`} />
              <AttachmentUploader attachments={attachments} setAttachments={setAttachments} testid={`comment-attachment-uploader-${d.id}`} />
            </div>
            <Button
              type="submit"
              disabled={sending}
              size="sm"
              className="bg-slate-900 hover:bg-slate-700 text-white shrink-0"
              data-testid={`comment-submit-${d.id}`}
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reply"}
            </Button>
          </div>
        </form>
      </div>
      </>
      )}
    </div>
  );
}

function TaskComments({ task, reload }) {
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [baseline] = useState(task.seen_at_me || "");
  const { user } = useAuth();
  const comments = task.comments || [];
  const isNewReply = (c) => c.created_by !== user?.id && !!c.created_at && (!baseline || c.created_at > baseline);

  const addComment = async (e) => {
    e.preventDefault();
    if (!body.trim() && images.length === 0 && attachments.length === 0) return;
    setSending(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, { body, images, attachments });
      setBody("");
      setImages([]);
      setAttachments([]);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-slate-100">
      <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-3">
        {comments.length} {comments.length === 1 ? "reply" : "replies"}
      </p>

      {comments.length > 0 && (
        <div className="space-y-4 mb-4" data-testid={`task-comments-${task.id}`}>
          {comments.map((c) => (
            <div key={c.id} className={`flex gap-3 rounded-lg -mx-2 px-2 py-1.5 transition-colors ${isNewReply(c) ? "bg-red-50 ring-1 ring-red-200" : ""}`} data-testid={`task-comment-${c.id}`}>
              <div className={`h-8 w-8 rounded-full ${c.created_by_role === "admin" ? "bg-amber-500" : "bg-indigo-600"} text-white text-xs flex items-center justify-center shrink-0`}>
                {c.created_by_name?.[0] || "U"}
              </div>
              <div className="min-w-0 flex-1">
                <Reply c={c} endpoint={`/tasks/${task.id}/comments/${c.id}`} reload={reload} header={<>
                  <p className="text-sm font-medium text-slate-900">{c.created_by_name}</p>
                  <span className="text-xs text-slate-400" data-testid={`task-comment-date-${c.id}`}>{formatDate(c.created_at)}</span>
                  {isNewReply(c) && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600 bg-red-100 rounded-full px-1.5 py-0.5" data-testid={`task-comment-new-${c.id}`}>New</span>
                  )}
                </>} />
                <ImageGrid images={c.images} />
                <AttachmentList attachments={c.attachments} />
                <div className="mt-2">
                  <LikeButton likes={c.likes} endpoint={`/tasks/${task.id}/comments/${c.id}/like`} reload={reload} testid={`task-comment-like-${c.id}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={addComment} className="space-y-3" data-testid={`task-comment-form-${task.id}`}>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a reply…"
          rows={2}
          data-testid={`task-comment-input-${task.id}`}
        />
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-2">
            <ImageUploader images={images} setImages={setImages} testid={`task-comment-image-uploader-${task.id}`} />
            <AttachmentUploader attachments={attachments} setAttachments={setAttachments} testid={`task-comment-attachment-uploader-${task.id}`} />
          </div>
          <Button
            type="submit"
            disabled={sending}
            size="sm"
            className="bg-slate-900 hover:bg-slate-700 text-white shrink-0"
            data-testid={`task-comment-submit-${task.id}`}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reply"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function EmptyState({ label, hint }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-xl bg-white py-16 flex flex-col items-center text-center">
      <p className="text-slate-900 font-medium">{label}</p>
      <p className="text-slate-500 text-sm mt-1">{hint}</p>
    </div>
  );
}

function TaskEditButton({ task, users, reload }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || "");
  const [assignee, setAssignee] = useState(task.assignee_id || "unassigned");
  const [priority, setPriority] = useState(task.priority || "medium");
  const [dueDate, setDueDate] = useState(task.due_date || "");
  const [images, setImages] = useState(task.images || []);
  const [attachments, setAttachments] = useState(task.attachments || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(task.title);
      setDescription(task.description || "");
      setAssignee(task.assignee_id || "unassigned");
      setPriority(task.priority || "medium");
      setDueDate(task.due_date || "");
      setImages(task.images || []);
      setAttachments(task.attachments || []);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/tasks/${task.id}`, {
        title,
        description,
        assignee_id: assignee === "unassigned" ? null : assignee,
        priority,
        due_date: dueDate || null,
        images,
        attachments,
      });
      toast.success("Task updated");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="text-slate-300 hover:text-slate-700 transition-colors p-1"
          aria-label="Edit task"
          data-testid={`edit-task-${task.id}`}
        >
          <Pencil className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit task</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid={`task-edit-form-${task.id}`}>
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid={`task-edit-title-${task.id}`} />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid={`task-edit-desc-${task.id}`} />
          </div>
          <div className="space-y-2">
            <Label>Assign to</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger data-testid={`task-edit-assignee-${task.id}`}>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PriorityDueFields priority={priority} setPriority={setPriority} dueDate={dueDate} setDueDate={setDueDate} prefix={`task-edit-${task.id}`} />
          <div className="space-y-2">
            <Label>Images</Label>
            <ImageUploader images={images} setImages={setImages} testid={`task-edit-image-uploader-${task.id}`} />
          </div>
          <div className="space-y-2">
            <Label>Attachments</Label>
            <AttachmentUploader attachments={attachments} setAttachments={setAttachments} testid={`task-edit-attachment-uploader-${task.id}`} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid={`task-edit-submit-${task.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DiscussionEditButton({ d, reload }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(d.title);
  const [body, setBody] = useState(d.body || "");
  const [images, setImages] = useState(d.images || []);
  const [attachments, setAttachments] = useState(d.attachments || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(d.title);
      setBody(d.body || "");
      setImages(d.images || []);
      setAttachments(d.attachments || []);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/discussions/${d.id}`, { title, body, images, attachments });
      toast.success("Discussion updated");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="text-slate-300 hover:text-slate-700 transition-colors p-1"
          aria-label="Edit discussion"
          data-testid={`edit-discussion-${d.id}`}
        >
          <Pencil className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit discussion</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid={`discussion-edit-form-${d.id}`}>
          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required data-testid={`discussion-edit-title-${d.id}`} />
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid={`discussion-edit-body-${d.id}`} />
          </div>
          <div className="space-y-2">
            <Label>Images</Label>
            <ImageUploader images={images} setImages={setImages} testid={`discussion-edit-image-uploader-${d.id}`} />
          </div>
          <div className="space-y-2">
            <Label>Attachments</Label>
            <AttachmentUploader attachments={attachments} setAttachments={setAttachments} testid={`discussion-edit-attachment-uploader-${d.id}`} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid={`discussion-edit-submit-${d.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
